import { useEffect, useRef, useState } from 'react';
import { formatDate } from '../dates.ts';
import { useSession } from '../auth/session.tsx';
import { Refusal } from './PropertiesPanel.tsx';
import { RecordEdit } from './RecordEdit.ts';
import { ReasonFields, useReasonReady } from './VisitDetails.tsx';
import { centsToText, formatCents, formatMoneyText, lastFour, toCents } from '../money.ts';
import { formatMoneyInput, moneyText } from '../moneyInput.ts';
import { MoneyBox } from './MoneyBox.tsx';
import {
  revealAccountNumber, useAccountReveals, useAddFullNumber, useClientLedger, useCorrectEntry, useEntryVersions,
  useLedgerEntries, useOpenAdvances, useReconciliation, useRecordEntry, useRecordStatement, useWithdrawEntry,
  useWithdrawStatement,
  type EntryFields, type EntryMethod, type LedgerAccount, type LedgerCategory, type LedgerEntry, type LedgerRights,
} from '../api/ledger.ts';

/**
 * The client's Finance section, money phase 4 (plan R4, a198aa36): ACCOUNTS (A1, with the reveal, A5), ENTRIES (A2,
 * with "Money owed to Allied" above them, R4), and RECONCILIATION (A4). Every button is drawn from the server's may_*
 * flags (slice 1's ledgerRights()), so a button and the route never disagree. An account is "...1234" everywhere; its
 * full number exists only inside RevealedNumber, for at most 30 seconds.
 */

const field = 'mt-1 w-full min-h-[48px] rounded-lg border border-neutral-300 bg-white px-3 text-base text-neutral-900';
const label = 'block text-sm font-semibold text-neutral-700';
const hint = 'mt-1 text-sm text-neutral-600';
const primary = 'min-h-[44px] rounded-lg bg-neutral-900 px-4 text-sm font-semibold text-white disabled:bg-neutral-200 disabled:text-neutral-600 disabled:cursor-not-allowed';
const secondary = 'min-h-[44px] rounded-lg border border-neutral-300 px-3 text-sm font-semibold text-neutral-800 hover:bg-neutral-50';

function todayLocal(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(new Date());
}
const accountWords = (a: Pick<LedgerAccount, 'institution_name' | 'kind' | 'last4'>) =>
  `${a.institution_name}${a.kind ? ` ${a.kind}` : ''} ${lastFour(a.last4)}`;

// ---------------------------------------------------------------------------- A1 and A5: the accounts
export function AccountsPanel({ clientId }: { clientId: string }) {
  const q = useClientLedger(clientId);
  if (q.error && !q.data) return <div className="px-4 pb-3"><Refusal error={q.error} /></div>;
  if (!q.data) return <p className="px-4 pb-3 text-neutral-600">Loading the accounts…</p>;
  const { accounts, may } = q.data;
  if (accounts.length === 0) {
    return <p className="px-4 pb-3 text-neutral-600">No account is recorded. Accounts are added on the Finance section's Accounts and marshalling panel.</p>;
  }
  return (
    <ul className="divide-y divide-neutral-100 border-t border-neutral-200">
      {accounts.map((a) => <li key={a.id} className="px-4 py-3"><AccountRow clientId={clientId} a={a} may={may} /></li>)}
    </ul>
  );
}

function AccountRow({ clientId, a, may }: { clientId: string; a: LedgerAccount; may: LedgerRights }) {
  const [adding, setAdding] = useState(false);
  const [seeing, setSeeing] = useState(false);
  const diff = toCents(a.difference);
  return (
    <div>
      <p className="font-semibold text-neutral-900">
        {a.institution_name}{a.kind ? ` · ${a.kind}` : ''} · <RevealedNumber accountId={a.id} last4={a.last4}
          mayReveal={may.reveal && a.number_on_file && !a.withdrawn} />
      </p>
      <p className="text-sm text-neutral-700">
        {a.withdrawn ? 'Withdrawn: entered in error.' : a.closed_on ? `Closed ${formatDate(a.closed_on)}${a.closed_reason ? ` (${a.closed_reason})` : ''}.` : 'Open.'}
        {' '}Balance by the entries today: {formatMoneyText(a.balance_today)}.
      </p>
      <p className="text-sm text-neutral-700">
        {a.statement_as_of
          ? <>Statement of {formatDate(a.statement_as_of)}: {formatMoneyText(a.statement_balance)}; the entries on that day:
              {' '}{formatMoneyText(a.ledger_balance_then)}. {diff === 0n ? 'Agrees.' : `Differs by ${formatCents(diff)}.`}</>
          : 'No statement balance recorded yet.'}
      </p>
      <div className="mt-1 flex flex-wrap gap-2">
        {may.record && !a.number_on_file && !a.withdrawn && !adding && (
          <button type="button" className={secondary} onClick={() => setAdding(true)}>Add the full number</button>
        )}
        {may.see_reveals && a.number_on_file && (
          <button type="button" className={secondary} aria-expanded={seeing} onClick={() => setSeeing(!seeing)}>
            Who has seen this number
          </button>
        )}
      </div>
      {adding && <FullNumberForm clientId={clientId} a={a} onDone={() => setAdding(false)} />}
      {seeing && <RevealHistory clientId={clientId} accountId={a.id} />}
    </div>
  );
}

/**
 * A5, 120's model: "Show full number" asks the server, which writes the audit row and commits before it answers. The
 * number is held in THIS component's state only (never the query cache, the address, storage or a tab) and is gone
 * after 30 seconds, on Hide, when the component unmounts, or before printing (beforeprint); print:hidden is a second guard.
 */
export const REVEAL_SECONDS = 30;
export function RevealedNumber({ accountId, last4, mayReveal }: { accountId: string; last4: string | null; mayReveal: boolean }) {
  const { getToken } = useSession();
  const [shown, setShown] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hide = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    setShown(null);
  };
  // unmount: the number goes with the component
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  // printing: the number leaves the DOM before anything prints (A-#407: in NO node on print); print:hidden stays as a
  // second guard
  useEffect(() => {
    const beforePrint = () => hide();
    window.addEventListener('beforeprint', beforePrint);
    return () => window.removeEventListener('beforeprint', beforePrint);
  }, []);
  const reveal = async () => {
    setError(null);
    try {
      const n = await revealAccountNumber(accountId, getToken);
      setShown(n);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(hide, REVEAL_SECONDS * 1000);
    } catch (e) {
      setError(e as Error);
    }
  };
  return (
    <span>
      {shown === null ? <span className="tabular-nums">{lastFour(last4)}</span> : (
        <>
          <span className="tabular-nums print:hidden" data-full-number>{shown}</span>
          <span className="hidden tabular-nums print:inline">{lastFour(last4)}</span>
        </>
      )}
      {mayReveal && (shown === null
        ? <button type="button" className="ml-2 min-h-[44px] text-sm font-semibold underline underline-offset-2" onClick={() => void reveal()}>Show full number</button>
        : <button type="button" className="ml-2 min-h-[44px] text-sm font-semibold underline underline-offset-2" onClick={hide}>Hide</button>)}
      {error && <span className="block text-sm font-normal"><Refusal error={error} /></span>}
    </span>
  );
}

function FullNumberForm({ clientId, a, onDone }: { clientId: string; a: LedgerAccount; onDone: () => void }) {
  const add = useAddFullNumber();
  const input = useRef<HTMLInputElement>(null);
  const id = `number-${a.id}`;
  return (
    <form className="mt-2 space-y-3 rounded-lg border border-neutral-300 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            const n = input.current?.value ?? '';
            if (!n.trim()) return;
            // the number is read from the field once and the field emptied: it is never in this page's state
            if (input.current) input.current.value = '';
            add.mutate({ clientId, accountId: a.id, number: n }, { onSuccess: () => onDone() });
          }}>
      <h3 className="font-semibold text-neutral-900">The full number for {accountWords(a)}</h3>
      <p className={hint}>Its last four must be {a.last4 ?? 'the account\'s'}; the number is kept encrypted and shown only on request.</p>
      <div>
        <label className={label} htmlFor={id}>Full account number</label>
        <input id={id} ref={input} type="password" inputMode="numeric" autoComplete="off" className={field} maxLength={64} />
      </div>
      {add.error && <Refusal error={add.error} />}
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={primary} disabled={add.isPending}>Record it</button>
        <button type="button" className={secondary} onClick={onDone}>Cancel</button>
      </div>
    </form>
  );
}

function RevealHistory({ clientId, accountId }: { clientId: string; accountId: string }) {
  const q = useAccountReveals(clientId, accountId, true);
  if (q.error && !q.data) return <Refusal error={q.error} />;
  if (!q.data) return <p className={hint}>Loading…</p>;
  if (q.data.reveals.length === 0) return <p className={hint}>Nobody has seen the full number.</p>;
  return (
    <ul className="mt-1 text-sm text-neutral-800">
      {q.data.reveals.map((r) => <li key={`${r.at}-${r.by_name}`}>{r.by_name}, {new Date(r.at).toLocaleString()}</li>)}
    </ul>
  );
}

// ---------------------------------------------------------------------------- R4: money owed to Allied
/**
 * "Money owed to Allied: $X" (D-125 15): the open advances' total and lines, from slice 2. A DISPLAY LINE: it is not
 * an entry, moves no balance, is in no running total, and carries no court-schedule line. Nothing is drawn when
 * nothing is owed, or while slice 2's route is absent (it is not yet built).
 */
export function MoneyOwedToAllied({ clientId }: { clientId: string }) {
  const q = useOpenAdvances(clientId);
  const owed = q.data;
  if (!owed || owed.open.length === 0) return null;
  return (
    <section aria-labelledby={`owed-${clientId}`} className="border-b border-neutral-200 px-4 py-3">
      <h3 id={`owed-${clientId}`} className="font-semibold text-neutral-900">Money owed to Allied: {formatMoneyText(owed.owed)}</h3>
      <p className={hint}>Advances not yet reimbursed. Not a ledger entry: it is in no balance and no court schedule.</p>
      <ul className="mt-1 text-sm text-neutral-800">
        {owed.open.map((a) => (
          <li key={a.id}>{formatDate(a.advanced_on)} · {a.reason} · {formatMoneyText(a.outstanding)} outstanding</li>
        ))}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------- A2: the entries
const PAGE = 50;
const METHODS: { value: EntryMethod; label: string }[] = [
  { value: 'check', label: 'Check' }, { value: 'ach', label: 'ACH' }, { value: 'online', label: 'Online' },
  { value: 'cash', label: 'Cash' }, { value: 'transfer', label: 'Transfer' }, { value: 'other', label: 'Other' },
];

export function EntriesPanel({ clientId }: { clientId: string }) {
  const ledger = useClientLedger(clientId);
  const [account, setAccount] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [category, setCategory] = useState('');
  const [offset, setOffset] = useState(0);
  const [recording, setRecording] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const q = useLedgerEntries(clientId, {
    account: account || undefined, from: from || undefined, to: to || undefined, category: category || undefined,
    limit: PAGE, offset,
  }, !!ledger.data);
  if (ledger.error && !ledger.data) return <div className="px-4 pb-3"><Refusal error={ledger.error} /></div>;
  if (!ledger.data) return <p className="px-4 pb-3 text-neutral-600">Loading…</p>;
  const { accounts, categories, may } = ledger.data;
  const open = accounts.filter((a) => !a.withdrawn);
  const page = q.data;
  return (
    <div>
      <MoneyOwedToAllied clientId={clientId} />
      <div className="flex flex-wrap items-end gap-2 px-4 pt-3">
        <div>
          <label className={label} htmlFor={`entries-account-${clientId}`}>Account</label>
          <select id={`entries-account-${clientId}`} className={field} value={account}
                  onChange={(e) => { setAccount(e.target.value); setOffset(0); }}>
            <option value="">Every account</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{accountWords(a)}</option>)}
          </select>
        </div>
        <div>
          <label className={label} htmlFor={`entries-from-${clientId}`}>From</label>
          <input id={`entries-from-${clientId}`} type="date" className={field} value={from} onChange={(e) => { setFrom(e.target.value); setOffset(0); }} />
        </div>
        <div>
          <label className={label} htmlFor={`entries-to-${clientId}`}>To</label>
          <input id={`entries-to-${clientId}`} type="date" className={field} value={to} onChange={(e) => { setTo(e.target.value); setOffset(0); }} />
        </div>
        <div>
          <label className={label} htmlFor={`entries-category-${clientId}`}>Category</label>
          <select id={`entries-category-${clientId}`} className={field} value={category}
                  onChange={(e) => { setCategory(e.target.value); setOffset(0); }}>
            <option value="">Every category</option>
            {categories.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
          </select>
        </div>
        {may.record && open.length > 0 && !recording && (
          <button type="button" className={secondary} onClick={() => setRecording(true)}>Record an entry</button>
        )}
      </div>
      {said && <p role="status" className="px-4 pt-2 text-sm text-neutral-800">{said}</p>}
      {recording && (
        <div className="px-4"><EntryForm clientId={clientId} accounts={open} categories={categories}
          onDone={(m) => { setRecording(false); if (m) setSaid(m); }} /></div>
      )}
      {q.error && !page ? <div className="px-4 py-3"><Refusal error={q.error} /></div> : !page ? (
        <p className="px-4 py-3 text-neutral-600">Loading the entries…</p>
      ) : page.entries.length === 0 ? (
        <p className="px-4 py-3 text-neutral-600">No entry matches.</p>
      ) : (
        <>
          <p className="px-4 pt-3 text-sm text-neutral-700">
            {/* A-#407: the net of the filtered set, labelled so nobody reads it as the account's balance (that is the
                server's account_balance(), on the account) */}
            {page.total.toLocaleString()} {page.total === 1 ? 'entry' : 'entries'}. Net of the entries shown: {formatMoneyText(page.net)}
            {' '}(every page of these filters, withdrawn entries left out; not an account's balance).
          </p>
          <ul className="divide-y divide-neutral-100">
            {page.entries.map((e) => (
              <li key={e.id} className="px-4 py-2">
                <EntryRow clientId={clientId} e={e} accounts={accounts} categories={categories} may={may} onSaid={setSaid} />
              </li>
            ))}
          </ul>
          <div className="flex gap-2 px-4 pb-3">
            <button type="button" className={secondary} disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>Newer</button>
            <button type="button" className={secondary} disabled={offset + PAGE >= page.total} onClick={() => setOffset(offset + PAGE)}>Older</button>
          </div>
        </>
      )}
    </div>
  );
}

/** Where an entry's category feeds the court account; a category with none reads "not yet decided" (M7, R2). */
function scheduleWords(c: LedgerCategory | undefined): string {
  if (!c || !c.schedule) return 'Court schedule: not yet decided';
  return `Court schedule ${c.schedule}${c.schedule_line ? `, line ${c.schedule_line}` : ''}`;
}

function EntryRow({ clientId, e, accounts, categories, may, onSaid }: {
  clientId: string; e: LedgerEntry; accounts: LedgerAccount[]; categories: LedgerCategory[]; may: LedgerRights;
  onSaid: (m: string) => void;
}) {
  const [mode, setMode] = useState<'none' | 'correct' | 'withdraw'>('none');
  const [history, setHistory] = useState(false);
  const cents = toCents(e.amount);
  const account = accounts.find((a) => a.id === e.account_id);
  const cat = categories.find((c) => c.code === e.category_code);
  return (
    <div className={e.withdrawn ? 'text-neutral-600' : ''}>
      <p className="flex flex-wrap gap-x-3 text-neutral-900">
        <span className="tabular-nums">{formatDate(e.posted_on)}</span>
        <span className="font-semibold">{e.counterparty ?? 'No payee'}</span>
        <span>{e.category_label}</span>
        <span className="tabular-nums">{cents !== null && cents >= 0n ? `In ${formatCents(cents)}` : `Out ${formatCents(cents === null ? null : -cents)}`}</span>
      </p>
      <p className="text-sm text-neutral-700">
        {account ? accountWords(account) : 'An account'}
        {e.method ? ` · ${METHODS.find((m) => m.value === e.method)?.label ?? e.method}` : ''}
        {e.check_number ? ` · check ${e.check_number}` : ''}
        {e.memo ? ` · ${e.memo}` : ''}
        {' · '}{scheduleWords(cat)}
        {e.receipt_upload_id || e.receipt_clio_copy_id ? ' · receipt on file' : ''}
        {e.source === 'import' ? ' · imported' : ''}
      </p>
      {e.withdrawn && <p className="text-sm text-neutral-700">Withdrawn: {e.withdrawal_reason}. It counts for nothing.</p>}
      {mode === 'none' && (
        <div className="mt-1 flex flex-wrap gap-2">
          {e.current_version > 1 && (
            <button type="button" className={secondary} aria-expanded={history} onClick={() => setHistory(!history)}>History</button>
          )}
          {/* the one Edit menu every record uses; drawn for tiers 1-2 and AP/AR only (D-123), the server's may.correct */}
          <RecordEdit may={may.correct && !e.withdrawn} className={secondary} choices={[
            { key: 'correct', label: 'Correct', onPick: () => setMode('correct') },
            { key: 'withdraw', label: 'Withdraw: recorded in error', onPick: () => setMode('withdraw') },
          ]} />
        </div>
      )}
      {history && <EntryHistory clientId={clientId} entryId={e.id} />}
      {mode === 'correct' && (
        <EntryForm clientId={clientId} accounts={accounts.filter((a) => !a.withdrawn)} categories={categories} entry={e}
                   onDone={(m) => { setMode('none'); if (m) onSaid(m); }} />
      )}
      {mode === 'withdraw' && <WithdrawEntry clientId={clientId} e={e} onDone={(m) => { setMode('none'); if (m) onSaid(m); }} />}
    </div>
  );
}

function EntryHistory({ clientId, entryId }: { clientId: string; entryId: string }) {
  const q = useEntryVersions(clientId, entryId, true);
  if (q.error && !q.data) return <Refusal error={q.error} />;
  if (!q.data) return <p className={hint}>Loading…</p>;
  return (
    <ul className="mt-1 text-sm text-neutral-700">
      {q.data.versions.map((v) => (
        <li key={v.version}>
          Version {v.version}, {new Date(v.recorded_at).toLocaleDateString()}{v.by ? ` by ${v.by}` : ''}
          {v.withdrawn ? `: withdrawn (${v.withdrawal_reason})` : v.edit_reason ? `: ${v.edit_reason.toLowerCase().replace(/_/g, ' ')}${v.reason_text ? `, ${v.reason_text}` : ''}` : ': as recorded'}
        </li>
      ))}
    </ul>
  );
}

/** Record, or with `entry`, correct (a whole new version with a reason). The amount is typed positive; the direction signs it. */
function EntryForm({ clientId, accounts, categories, entry, onDone }: {
  clientId: string; accounts: LedgerAccount[]; categories: LedgerCategory[]; entry?: LedgerEntry; onDone: (m: string | null) => void;
}) {
  const start = entry ? toCents(entry.amount) : null;
  const [f, setF] = useState({
    account: entry?.account_id ?? accounts[0]?.id ?? '', direction: start !== null && start < 0n ? 'out' : 'in',
    postedOn: entry?.posted_on ?? '', amount: start !== null ? formatMoneyInput(centsToText(start < 0n ? -start : start)) : '',
    category: entry?.category_code ?? '', payee: entry?.counterparty ?? '', method: (entry?.method ?? '') as EntryMethod | '',
    check: entry?.check_number ?? '', memo: entry?.memo ?? '',
  });
  const [reason, setReason] = useState('');
  const [text, setText] = useState('');
  const ready = useReasonReady(reason, text);
  const record = useRecordEntry();
  const correct = useCorrectEntry();
  const act = entry ? correct : record;
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF((x) => ({ ...x, [k]: e.target.value }));
  // F-100: typed with commas or "$" or not; the direction signs it, so the box takes no "-"
  const cents = toCents(moneyText(f.amount));
  const usable = categories.filter((c) => (c.active || c.code === entry?.category_code)
    && (c.direction === 'either' || c.direction === f.direction));
  const ok = !!f.account && !!f.postedOn && cents !== null && cents > 0n && !!f.category && (!entry || ready);
  const id = `entry-${entry?.id ?? clientId}`;
  return (
    <form className="mt-2 space-y-3 rounded-lg border border-neutral-300 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!ok || cents === null) return;
            const fields: EntryFields = {
              accountId: f.account, postedOn: f.postedOn, amount: centsToText(f.direction === 'out' ? -cents : cents),
              category: f.category, counterparty: f.payee.trim() || null, memo: f.memo.trim() || null,
              method: f.method || null, checkNumber: f.check.trim() || null,
            };
            if (entry) {
              correct.mutate({ clientId, entryId: entry.id, fields, reason, ...(text.trim() ? { reasonText: text.trim() } : {}) },
                { onSuccess: () => onDone('Entry corrected: a new version, with its reason.') });
            } else {
              record.mutate({ clientId, fields }, { onSuccess: () => onDone('Entry recorded.') });
            }
          }}>
      <h3 className="font-semibold text-neutral-900">{entry ? 'Correct the entry' : 'Record an entry'}</h3>
      <div>
        <label className={label} htmlFor={`${id}-account`}>Account</label>
        <select id={`${id}-account`} className={field} value={f.account} onChange={set('account')}>
          {accounts.map((a) => <option key={a.id} value={a.id}>{accountWords(a)}</option>)}
        </select>
      </div>
      <fieldset>
        <legend className={label}>Money</legend>
        <div className="flex gap-4">
          {(['in', 'out'] as const).map((d) => (
            <label key={d} className="flex min-h-[44px] items-center gap-2">
              <input type="radio" name={`${id}-direction`} value={d} checked={f.direction === d}
                     onChange={() => setF((x) => ({ ...x, direction: d, category: '' }))} />
              {d === 'in' ? 'In' : 'Out'}
            </label>
          ))}
        </div>
      </fieldset>
      <div>
        <label className={label} htmlFor={`${id}-date`}>Date</label>
        <input id={`${id}-date`} type="date" className={field} value={f.postedOn} max={todayLocal()} onChange={set('postedOn')} />
      </div>
      <div>
        <label className={label} htmlFor={`${id}-amount`}>Amount</label>
        <MoneyBox id={`${id}-amount`} className={field} value={f.amount} onChange={(v) => setF((x) => ({ ...x, amount: v }))} />
      </div>
      <div>
        <label className={label} htmlFor={`${id}-category`}>Category</label>
        <select id={`${id}-category`} className={field} value={f.category} onChange={set('category')}>
          <option value="">Choose…</option>
          {usable.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
        </select>
      </div>
      <div>
        <label className={label} htmlFor={`${id}-payee`}>Payee or payer</label>
        <input id={`${id}-payee`} className={field} value={f.payee} maxLength={200} onChange={set('payee')} />
        <p className={hint}>Never an account or card number: a run of five or more digits is refused.</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className={label} htmlFor={`${id}-method`}>Method</label>
          <select id={`${id}-method`} className={field} value={f.method} onChange={set('method')}>
            <option value="">Not recorded</option>
            {METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>
        <div>
          <label className={label} htmlFor={`${id}-check`}>Check number</label>
          <input id={`${id}-check`} inputMode="numeric" className={field} value={f.check} maxLength={20} onChange={set('check')} />
        </div>
      </div>
      <div>
        <label className={label} htmlFor={`${id}-memo`}>Memo</label>
        <input id={`${id}-memo`} className={field} value={f.memo} maxLength={500} onChange={set('memo')} />
      </div>
      {entry && <ReasonFields reason={reason} text={text} setReason={setReason} setText={setText} idPrefix={id} />}
      {act.error && <Refusal error={act.error} />}
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={primary} disabled={!ok || act.isPending}>{entry ? 'Save the correction' : 'Record'}</button>
        <button type="button" className={secondary} onClick={() => onDone(null)}>Cancel</button>
      </div>
    </form>
  );
}

function WithdrawEntry({ clientId, e, onDone }: { clientId: string; e: LedgerEntry; onDone: (m: string | null) => void }) {
  const [why, setWhy] = useState('');
  const w = useWithdrawEntry();
  const id = `withdraw-${e.id}`;
  return (
    <form className="mt-2 space-y-3 rounded-lg border border-neutral-300 p-3"
          onSubmit={(ev) => {
            ev.preventDefault();
            if (!why.trim()) return;
            w.mutate({ clientId, entryId: e.id, reason: why.trim() }, { onSuccess: () => onDone('Entry withdrawn. It counts for nothing.') });
          }}>
      <h3 className="font-semibold text-neutral-900">Withdraw the entry</h3>
      <p className={hint}>For an entry recorded in error. It stays on the record and counts toward nothing.</p>
      <div>
        <label className={label} htmlFor={`${id}-why`}>Why</label>
        <input id={`${id}-why`} className={field} value={why} maxLength={2000} onChange={(ev) => setWhy(ev.target.value)} />
      </div>
      {w.error && <Refusal error={w.error} />}
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={primary} disabled={!why.trim() || w.isPending}>Withdraw</button>
        <button type="button" className={secondary} onClick={() => onDone(null)}>Cancel</button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------- A4: reconciliation
export function ReconciliationPanel({ clientId }: { clientId: string }) {
  const ledger = useClientLedger(clientId);
  if (!ledger.data) return null;
  const accounts = ledger.data.accounts.filter((a) => !a.withdrawn);
  if (accounts.length === 0) return <p className="px-4 pb-3 text-neutral-600">No account to reconcile.</p>;
  return (
    <ul className="divide-y divide-neutral-100 border-t border-neutral-200">
      {accounts.map((a) => <li key={a.id} className="px-4 py-3"><AccountReconciliation clientId={clientId} a={a} may={ledger.data!.may} /></li>)}
    </ul>
  );
}

function AccountReconciliation({ clientId, a, may }: { clientId: string; a: LedgerAccount; may: LedgerRights }) {
  const q = useReconciliation(clientId, a.id);
  const [recording, setRecording] = useState(false);
  const [withdrawing, setWithdrawing] = useState<string | null>(null);
  return (
    <div>
      <p className="font-semibold text-neutral-900">{accountWords(a)}</p>
      {q.error && !q.data ? <Refusal error={q.error} /> : !q.data ? <p className={hint}>Loading…</p>
        : q.data.statements.length === 0 ? <p className={hint}>No statement balance recorded yet.</p> : (
        <ul className="mt-1 space-y-1 text-sm text-neutral-800">
          {q.data.statements.map((s) => {
            const d = toCents(s.difference);
            return (
              <li key={s.statement_id}>
                {formatDate(s.as_of)}: the statement {formatMoneyText(s.statement_balance)}, the entries {formatMoneyText(s.ledger_balance)}
                {' · '}<span className="font-semibold">{d === 0n ? 'Agrees' : `Differs by ${formatCents(d)}`}</span>
                {' · '}{s.entries_since_previous} {s.entries_since_previous === 1 ? 'entry' : 'entries'} since the previous statement
                {' '}<RecordEdit may={may.correct} choices={[
                  { key: 'withdraw', label: 'Withdraw: recorded in error', onPick: () => setWithdrawing(s.statement_id) },
                ]} />
                {withdrawing === s.statement_id && <WithdrawStatement clientId={clientId} statementId={s.statement_id} onDone={() => setWithdrawing(null)} />}
              </li>
            );
          })}
        </ul>
      )}
      {may.record && !recording && (
        <button type="button" className={`${secondary} mt-2`} onClick={() => setRecording(true)}>Record a statement balance</button>
      )}
      {recording && <StatementForm clientId={clientId} a={a} onDone={() => setRecording(false)} />}
    </div>
  );
}

function StatementForm({ clientId, a, onDone }: { clientId: string; a: LedgerAccount; onDone: () => void }) {
  const [asOf, setAsOf] = useState('');
  const [balance, setBalance] = useState('');
  const [source, setSource] = useState<'statement' | 'bank letter'>('statement');
  const r = useRecordStatement();
  const id = `statement-${a.id}`;
  // F-100: an overdrawn account's balance may be negative
  const typed = moneyText(balance, { negative: true });
  const ok = !!asOf && toCents(typed) !== null;
  return (
    <form className="mt-2 space-y-3 rounded-lg border border-neutral-300 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            const c = toCents(typed);
            if (!ok || c === null) return;
            r.mutate({ clientId, accountId: a.id, asOf, balance: centsToText(c), source }, { onSuccess: onDone });
          }}>
      <h3 className="font-semibold text-neutral-900">Record a statement balance for {accountWords(a)}</h3>
      <div>
        <label className={label} htmlFor={`${id}-on`}>Balance as of</label>
        <input id={`${id}-on`} type="date" className={field} value={asOf} max={todayLocal()} onChange={(e) => setAsOf(e.target.value)} />
      </div>
      <div>
        <label className={label} htmlFor={`${id}-balance`}>Balance</label>
        <MoneyBox id={`${id}-balance`} className={field} value={balance} onChange={setBalance} negative />
      </div>
      <div>
        <label className={label} htmlFor={`${id}-source`}>From</label>
        <select id={`${id}-source`} className={field} value={source} onChange={(e) => setSource(e.target.value as 'statement' | 'bank letter')}>
          <option value="statement">The bank statement</option>
          <option value="bank letter">A letter from the bank</option>
        </select>
      </div>
      {r.error && <Refusal error={r.error} />}
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={primary} disabled={!ok || r.isPending}>Record</button>
        <button type="button" className={secondary} onClick={onDone}>Cancel</button>
      </div>
    </form>
  );
}

function WithdrawStatement({ clientId, statementId, onDone }: { clientId: string; statementId: string; onDone: () => void }) {
  const [why, setWhy] = useState('');
  const w = useWithdrawStatement();
  const id = `withdraw-statement-${statementId}`;
  return (
    <form className="mt-2 space-y-3 rounded-lg border border-neutral-300 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!why.trim()) return;
            w.mutate({ clientId, statementId, reason: why.trim() }, { onSuccess: onDone });
          }}>
      <div>
        <label className={label} htmlFor={`${id}-why`}>Why it is withdrawn</label>
        <input id={`${id}-why`} className={field} value={why} maxLength={2000} onChange={(e) => setWhy(e.target.value)} />
      </div>
      {w.error && <Refusal error={w.error} />}
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={primary} disabled={!why.trim() || w.isPending}>Withdraw</button>
        <button type="button" className={secondary} onClick={onDone}>Cancel</button>
      </div>
    </form>
  );
}
